import { useEffect, useRef } from 'react';
import { useApiClient } from '@tkpod/api-client';
import {
  ADD_TO_ORDER_EVENT,
  createPortalTransport,
  defineTkProductOptions,
  type AddToOrderDetail,
  type TkProductOptionsElement,
} from '@tkpod/options-widget';

/**
 * Mounts the shared options element with the portal session.
 * The element is created after the transport is set so the first
 * config request uses the authenticated client.
 */
export function PortalProductOptions({
  uptId,
  onAddToOrder,
}: {
  uptId: string;
  onAddToOrder: (detail: AddToOrderDetail) => void;
}) {
  const api = useApiClient();
  const hostRef = useRef<HTMLDivElement>(null);
  const onAddRef = useRef(onAddToOrder);
  onAddRef.current = onAddToOrder;

  useEffect(() => {
    defineTkProductOptions();
    const host = hostRef.current;
    if (!host) return;

    const element = document.createElement('tk-product-options') as TkProductOptionsElement;
    element.setAttribute('surface', 'portal');
    element.setAttribute('upt-id', uptId);
    element.setAttribute('action-label', 'Add to Order');
    element.setAttribute('money-format', '${{amount}}');
    element.setTransport(createPortalTransport(async (path, init) => {
      if (init.method === 'GET') return api.get(path);
      return api.post(path, init.body, { headers: init.headers });
    }));

    const onEvent = (event: Event) => {
      const detail = (event as CustomEvent<AddToOrderDetail>).detail;
      if (!detail?.optionSubmissionId) return;
      onAddRef.current(detail);
    };
    element.addEventListener(ADD_TO_ORDER_EVENT, onEvent);
    host.replaceChildren(element);

    return () => {
      element.removeEventListener(ADD_TO_ORDER_EVENT, onEvent);
      if (host.contains(element)) host.removeChild(element);
    };
  }, [api, uptId]);

  return <div ref={hostRef} />;
}
